// src/components/pos/ProductSelectorDialog.jsx
// 无规格商品的「散装/整箱 + 零售/VIP + 数量」选择面板
// 原本内嵌在 ProductGrid.jsx 里，现抽出独立组件，由 POSPage 调用
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const ProductSelectorDialog = ({ product, onConfirm, onClose }) => {
  const { language } = useApp();
  const [sellType, setSellType]   = useState('unit');   // 'unit' | 'box'
  const [priceType, setPriceType] = useState('retail'); // 'retail' | 'vip'
  const [quantity, setQuantity]   = useState(1);

  const hasBox = product.box_quantity > 0;
  const hasVip = sellType === 'unit' ? !!product.vip_price : !!product.vip_box_price;

  // 当前单价
  const getUnitPrice = () => {
    if (sellType === 'box') {
      return priceType === 'vip' && product.vip_box_price
        ? parseFloat(product.vip_box_price)
        : parseFloat(product.box_price || 0);
    }
    return priceType === 'vip' && product.vip_price
      ? parseFloat(product.vip_price)
      : parseFloat(product.sale_price || 0);
  };

  const unitPrice = getUnitPrice();
  const subtotal  = unitPrice * quantity;

  // 库存换算
  const stockInUnits = product.stock || 0;
  const maxQty = sellType === 'box' && product.box_quantity
    ? Math.floor(stockInUnits / product.box_quantity)
    : stockInUnits;

  const getProductName = () => {
    if (language === 'en') return product.name_en || product.name;
    if (language === 'km') return product.name_km || product.name;
    return product.name;
  };

  const handleConfirm = () => {
    if (quantity <= 0) return;
    onConfirm({
      product_id:      product.id,
      product_name:    product.name,
      product_name_en: product.name_en,
      product_name_km: product.name_km,
      sell_type:       sellType,
      price_type:      priceType,
      unit_price:      unitPrice,
      quantity,
      box_quantity:    sellType === 'box' ? product.box_quantity : null,
      barcode:         product.barcode,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl"
        onClick={e => e.stopPropagation()}>

        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h4 className="font-bold text-gray-800">{getProductName()}</h4>
            {product.barcode && <p className="text-xs text-gray-400">{product.barcode}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">

          {/* 售卖单位 */}
          <div>
            <p className="text-sm font-medium text-gray-600 mb-2">售卖单位</p>
            <div className="flex gap-2">
              <button
                onClick={() => { setSellType('unit'); setPriceType('retail'); setQuantity(1); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                  sellType === 'unit'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                }`}>
                散装（个）
              </button>
              {hasBox && (
                <button
                  onClick={() => { setSellType('box'); setPriceType('retail'); setQuantity(1); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                    sellType === 'box'
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                  }`}>
                  整箱（{product.box_quantity}个/箱）
                </button>
              )}
            </div>
          </div>

          {/* 价格类型 */}
          <div>
            <p className="text-sm font-medium text-gray-600 mb-2">价格类型</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPriceType('retail')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                  priceType === 'retail'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                }`}>
                <div>{sellType === 'box' ? '普通箱价' : '零售价'}</div>
                <div className="text-xs opacity-80">
                  ${sellType === 'box'
                    ? parseFloat(product.box_price || 0).toFixed(2)
                    : parseFloat(product.sale_price || 0).toFixed(2)}
                  {sellType === 'box' ? '/箱' : '/个'}
                </div>
              </button>
              {hasVip && (
                <button
                  onClick={() => setPriceType('vip')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                    priceType === 'vip'
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'
                  }`}>
                  <div>{sellType === 'box' ? 'VIP箱价' : 'VIP价'}</div>
                  <div className="text-xs opacity-80">
                    ${sellType === 'box'
                      ? parseFloat(product.vip_box_price || 0).toFixed(2)
                      : parseFloat(product.vip_price || 0).toFixed(2)}
                    {sellType === 'box' ? '/箱' : '/个'}
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* 数量选择 */}
          <div>
            <p className="text-sm font-medium text-gray-600 mb-2">
              数量
              <span className="ml-2 text-xs text-gray-400">
                （库存：{sellType === 'box'
                  ? `${maxQty} 箱（${stockInUnits} 个）`
                  : `${stockInUnits} 个`}）
              </span>
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center font-bold text-lg">
                −
              </button>
              <input
                type="number"
                min="1"
                max={maxQty}
                value={quantity}
                onChange={e => setQuantity(Math.min(maxQty, Math.max(1, parseInt(e.target.value) || 1)))}
                className="flex-1 text-center text-xl font-bold border rounded-lg py-2 focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={() => setQuantity(q => Math.min(maxQty, q + 1))}
                className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center font-bold text-lg">
                ＋
              </button>
            </div>
          </div>

          {/* 小计 */}
          <div className="bg-gray-50 rounded-xl p-3 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              ${unitPrice.toFixed(2)} × {quantity} {sellType === 'box' ? '箱' : '个'}
            </div>
            <div className="text-xl font-bold text-blue-600">
              ${subtotal.toFixed(2)}
            </div>
          </div>

          {/* 确认按钮 */}
          <button
            onClick={handleConfirm}
            disabled={maxQty === 0}
            className="w-full py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
            {maxQty === 0 ? '库存不足' : '加入购物车'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelectorDialog;